/**
 * DebtTimelineChart — Recharts ComposedChart showing debt capital over time.
 *
 * Two lines:
 *   - Solid:   theoretical capital remaining (from schedule)
 *   - Dashed:  actual capital remaining (capital − cumulative linked transactions)
 *
 * Props:
 *   schedule     : [{date, capital_remaining_cents}]
 *   transactions : [{operation_date, settled_at, amount_cents}] — linked tx
 *   startDate    : 'YYYY-MM-DD'
 *   endDate      : 'YYYY-MM-DD'
 *   capital      : number — initial capital in cents
 */

import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { formatCurrency, formatCurrencyShort } from '../lib/format.js';

const TODAY = new Date().toISOString().slice(0, 10);

function formatXLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' });
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const items = payload.filter((p) => p.value != null);
  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2 text-xs min-w-[200px]">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {items.map((p) => (
        <p key={p.dataKey} style={{ color: p.stroke }} className="flex justify-between gap-3">
          <span>{p.name}</span>
          <span className="tabular-nums font-medium">{formatCurrency(p.value * 100)}</span>
        </p>
      ))}
    </div>
  );
}

/**
 * Alloue les tx liées aux lignes du schedule par matching de date (±20 j,
 * chronologique, sans double-match). Même logique que DebtScheduleTable.
 */
function computePaidIndices(schedule, transactions) {
  const paid = new Set();
  if (!Array.isArray(transactions) || !transactions.length) return paid;
  const usedTx = new Set();
  const sortedRows = [...schedule]
    .filter((r) => (r.total_cents ?? 0) > 0)
    .sort((a, b) => (a.date ?? '').localeCompare(b.date ?? ''));
  const sortedTxs = [...transactions]
    .filter((t) => t.operation_date || t.settled_at)
    .sort((a, b) => {
      const da = (a.operation_date ?? a.settled_at ?? '');
      const db = (b.operation_date ?? b.settled_at ?? '');
      return da.localeCompare(db);
    });
  for (const row of sortedRows) {
    const rowMs = new Date(row.date + 'T00:00:00').getTime();
    let bestTx = null;
    let bestDiff = Infinity;
    for (const tx of sortedTxs) {
      if (usedTx.has(tx.id)) continue;
      const txDateStr = (tx.operation_date ?? tx.settled_at ?? '').slice(0, 10);
      if (!txDateStr) continue;
      const txMs = new Date(txDateStr + 'T00:00:00').getTime();
      const diffDays = Math.abs((txMs - rowMs) / 86400000);
      if (diffDays <= 20 && diffDays < bestDiff) {
        bestDiff = diffDays;
        bestTx = tx;
      }
    }
    if (bestTx) {
      paid.add(row.month_index);
      usedTx.add(bestTx.id);
    }
  }
  return paid;
}

function buildChartData(schedule, transactions, capital) {
  const paidIndices = computePaidIndices(schedule, transactions);

  // Capital réel : on consomme la part PRINCIPAL des lignes marquées payées
  // (matching ±20 j). Comparable directement à capital_remaining_cents du
  // schedule — si tu paies selon le plan, les deux courbes se superposent
  // exactement.
  let cumPrincipalPaid = 0;
  return schedule.map((row) => {
    const dateStr = row.date?.slice(0, 10) ?? '';
    if (paidIndices.has(row.month_index)) {
      cumPrincipalPaid += (row.principal_cents ?? 0);
    }

    const theoreticalEuros = (row.capital_remaining_cents ?? 0) / 100;
    const actualEuros = dateStr <= TODAY
      ? Math.max(0, (capital - cumPrincipalPaid) / 100)
      : null;

    return {
      label: formatXLabel(dateStr),
      date: dateStr,
      theoretical: theoreticalEuros,
      actual: actualEuros,
    };
  });
}

export default function DebtTimelineChart({
  schedule = [],
  transactions = [],
  capital = 0,
}) {
  if (!schedule.length) {
    return (
      <div className="flex items-center justify-center h-[220px] text-sm text-slate-400">
        Pas de données
      </div>
    );
  }

  const chartData = buildChartData(schedule, transactions, capital);

  // Find label closest to today for the ReferenceLine
  const todayEntry = chartData.find((d) => d.date >= TODAY);
  const todayLabel = todayEntry?.label ?? null;

  return (
    <div className="w-full" style={{ minHeight: 220 }}>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart
          data={chartData}
          margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
          />

          <YAxis
            tickFormatter={(v) => formatCurrencyShort(v * 100)}
            tick={{ fontSize: 11, fill: '#64748b' }}
            tickLine={false}
            axisLine={false}
            width={64}
          />

          <Tooltip content={<CustomTooltip />} />

          <Legend
            wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
          />

          {todayLabel && (
            <ReferenceLine
              x={todayLabel}
              stroke="#6366f1"
              strokeDasharray="4 3"
              strokeWidth={1.5}
              label={{ value: 'Auj.', position: 'insideTopRight', fontSize: 10, fill: '#6366f1' }}
            />
          )}

          {/* Theoretical capital remaining (solid) */}
          <Line
            type="monotone"
            dataKey="theoretical"
            name="Capital théorique"
            stroke="#475569"
            strokeWidth={2}
            dot={false}
            connectNulls
            activeDot={{ r: 4 }}
          />

          {/* Actual capital remaining (dashed, up to today) */}
          <Line
            type="monotone"
            dataKey="actual"
            name="Capital réel"
            stroke="#0ea5e9"
            strokeWidth={2}
            strokeDasharray="5 4"
            dot={false}
            connectNulls
            activeDot={{ r: 4 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
